/**
 * ステータス別のフロー作業ページ(2026-08-26 ページ分割)。page プロパティで内容が変わる:
 *   approve=「承認」: ① 部門長確認(部門長)+② 承認(承認者)
 *   pay    =「支払い」: ③ 支払い(経理・部門長)+③-2 仮払い(経理)+⑤ 立替精算(経理)
 *   receive=「受領」: 自分が受領を記録すべきもの(仮払い金の受け取り。立替の受領は経理の精算時にサイン)
 *   confirm=「完了確認」: ④ 完了確認(承認者)
 * 通知バッジは各ページに独立して付き(notifications.stage)、「詳細・操作」を開くまで消えない。
 * 各アクションは行の「詳細・操作」から開く**詳細ポップアップ内**で行う。差戻しは理由必須。
 */
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { formatCashAccountLabel } from '@core/lib/cashAccountLabel';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@core/auth/AuthProvider';
import { useMasters } from '@core/masters/MastersProvider';
import { useToast } from '@core/components/Toast';
import Modal from '@core/components/Modal';
import { addDays, todayStr, toShortDate, tsToShortDate } from '@core/lib/date';
import { formatYen } from '@core/lib/money';
import { fetchRecurringSettings, fetchUsers } from '@core/masters/masterRepo';
import { fetchCashAccounts } from '@core/cash/cashRepo';
import {
  approve,
  complete,
  fetchForApproval,
  fetchForCashHandover,
  fetchForCompletion,
  fetchForManagerCheck,
  fetchForPaymentByAccounting,
  fetchForPaymentByApprover,
  fetchForPaymentByBucho,
  fetchForReceive,
  fetchForReimbursement,
  fetchForSelfPayment,
  fetchRecurringForManagerCheck,
  recurringManagerCheck,
  handCash,
  managerCheck,
  reject,
} from './flowActions';
import { markReadExceptStage, markReadForTransaction, subscribeMyUnread } from './notificationsRepo';
import { isFlowStaleError, reportFlowResult } from './flowResult';
import { skipRecurringOccurrence } from '@core/recurring/postRecurring';
import { roleOf } from './applicationRepo';
import { PaymentModal, ReceiptAttachModal, StatusBadge } from './FlowParts';
import { useIsMobile } from '@core/lib/useIsMobile';
import ApplicationDetailModal from './ApplicationDetailModal';
import BatchSettleModal from './BatchSettleModal';
import MultiSelectFilter from '@core/components/MultiSelectFilter';
import { SheetTh, applySheet, countSheetFilters, type SheetColumn, type SheetFilters, type SheetSort } from '@core/components/SheetHeader';
import { tsToMillis } from '@core/lib/date';
import { PAYMENT_METHOD_LABELS } from '@core/lib/types';
import {
  PAYER_LABELS,
  type CashAccount,
  type Transaction,
} from '@core/lib/types';

/** 担当事業部が無いときに毎回新しい配列を作らない(useCallback の依存が揺れて再取得が続くのを防ぐ) */
const NO_DEPTS: import('@core/lib/types').DepartmentId[] = [];

/** recurring_check = 繰り返し支払いの部門長確認(経理が支払った後。2026-10-15) */
type Stage = 'manager' | 'recurring_check' | 'approve' | 'cash_hand' | 'pay' | 'complete' | 'reimburse' | 'receive';

/** メニューの4ページ(通知の stage と同じ値) */
export type FlowPage = 'approve' | 'pay' | 'receive' | 'confirm';

/** 並べ替え項目(申請一覧と同じ方式)。通知日=その権限にステータスが回ってきた日 */
const SORT_OPTIONS = [
  { key: 'notified', label: '通知日' },
  { key: 'applied', label: '申請日' },
  { key: 'amount', label: '金額' },
  { key: 'payment', label: '支払予定日' },
] as const;
type SortKey = (typeof SORT_OPTIONS)[number]['key'];

// 種別バッジは一覧から非表示(v3.8 2-3。doc_typeはDBに保持・詳細で確認可)
/** 仮払いモーダル(経理・仮払い)。仮払いに使う口座を選び、CFへ出金を計上する(v1.1 §3-C-4) */
function PrepayModal({
  transaction,
  onClose,
  onDone,
}: {
  transaction: Transaction;
  onClose: () => void;
  onDone: () => void;
}) {
  const { user } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const { companyName } = useMasters();
  const [cashAccountId, setCashAccountId] = useState('');
  const [accounts, setAccounts] = useState<CashAccount[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetchCashAccounts()
      // クレカで仮払い・精算(現金の受け渡し)はしないため除外。
      // 口座は申請の会社のもののみ(例: KMAの申請にSUMの口座は出さない。会社「その他」は全社表示)
      .then((list) =>
        setAccounts(
          list.filter(
            (a) =>
              a.active &&
              a.kind !== 'card' &&
              ((transaction.company_id ?? 'sum') === 'sonota' || a.company_id === (transaction.company_id ?? 'sum')),
          ),
        ),
      )
      .catch(() => setAccounts([]));
  }, []);

  async function submit() {
    if (!user) return;
    setBusy(true);
    try {
      const result = await handCash({ transaction, userId: user.id, cashAccountId });
      reportFlowResult(toast, `${transaction.code} の仮払いを記録しました(本人の受領待ち)`, result);
      onDone();
      onClose();
    } catch (e) {
      toast(e instanceof Error ? e.message : '仮払いの記録に失敗しました');
      // ほかの人が先に処理していた場合は、一覧を最新にして閉じる
      if (isFlowStaleError(e)) {
        onDone();
        onClose();
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal onClose={onClose} width="min(560px, 92vw)">
      <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 12 }}>
        <div>
          <h3>仮払い(本人へ現金を渡す)</h3>
          <div className="path" style={{ margin: '2px 0 0' }}>
            {transaction.code} / {formatYen(transaction.amount)}円
          </div>
        </div>
      </div>
      <div className="form-grid" style={{ gridTemplateColumns: '1fr' }}>
        <div>
          <label>
            仮払いに使う口座・現金<span className="req">必須</span>
          </label>
          <select value={cashAccountId} onChange={(e) => setCashAccountId(e.target.value)}>
            <option value="">選択してください</option>
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>
                {formatCashAccountLabel(a, companyName(a.company_id))}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <button className="btn-green" onClick={() => void submit()} disabled={busy || !cashAccountId}>
          {busy ? '処理中…' : '仮払いを記録'}
        </button>
        <button className="txn-link" onClick={() => navigate(`/receipt/${transaction.id}?mode=internal`)}>
          受領書を出力(本人が記入)
        </button>
      </div>
      <p className="hint" style={{ marginTop: 8 }}>
        受領書を印刷して現金と一緒に本人へ渡してください。本人が受領書に記入し、スキャンを添付して
        「受領を記録」すると支払いに進めます。
      </p>
    </Modal>
  );
}

/** ページを行き来したとき前回の内容を即表示するためのキャッシュ(2026-08-28)。
    まずキャッシュを描画し、裏で最新を取り直して置き換える(読み込み待ちをなくす)。
    さらに再読込中も画面を消さないため、支払いモーダル等が開いたまま保たれ、
    「添付したのに画面が更新されて消える」問題も解消される */
type FlowCache = {
  managerRows: Transaction[];
  /** 繰り返し支払いの部門長確認(2026-10-15) */
  recurringCheckRows?: Transaction[];
  approvalRows: Transaction[];
  paymentRows: Transaction[];
  completionRows: Transaction[];
  reimbursementRows: Transaction[];
  cashHandoverRows: Transaction[];
  receiveRows: Transaction[];
  selfPayRows: Transaction[];
  userNames: Map<string, string>;
};
const flowCache = new Map<string, FlowCache>();

/**
 * 月でまとめる(2026-09-25 経理要望)。
 * 支払い予定日が先の分(例: 末締め翌月末払い)が支払い一覧に1か月居座って作業の邪魔になるため、
 * 支払い画面の一覧を「支払い予定日の月」で区切り、月ごとに畳めるようにする。
 * 開閉と入/切はログイン中だけ覚える(業務データではないので Firestore には保存しない)
 */
const groupMonthCache = new Map<string, boolean>();
const monthOpenCache = new Map<string, [string, boolean][]>();
/** 支払い予定日が無い分のまとまり */
const NO_MONTH = 'none';
function monthTitle(m: string): string {
  return m === NO_MONTH ? '支払い予定日なし' : `${m.slice(0, 4)}年${Number(m.slice(5, 7))}月`;
}

export default function ApprovalFlowPage({ page }: { page: FlowPage }) {
  const { user, flowRole, can } = useAuth();
  const { departmentName, accountName, subAccountName, companyName, departments, companies } = useMasters();
  const toast = useToast();

  const cacheKey = `${user?.id ?? ''}:${page}`;
  const cached = flowCache.get(cacheKey);
  const [managerRows, setManagerRows] = useState<Transaction[]>(cached?.managerRows ?? []);
  const [recurringCheckRows, setRecurringCheckRows] = useState<Transaction[]>(cached?.recurringCheckRows ?? []);
  const [approvalRows, setApprovalRows] = useState<Transaction[]>(cached?.approvalRows ?? []);
  const [paymentRows, setPaymentRows] = useState<Transaction[]>(cached?.paymentRows ?? []);
  const [completionRows, setCompletionRows] = useState<Transaction[]>(cached?.completionRows ?? []);
  const [reimbursementRows, setReimbursementRows] = useState<Transaction[]>(cached?.reimbursementRows ?? []);
  const [cashHandoverRows, setCashHandoverRows] = useState<Transaction[]>(cached?.cashHandoverRows ?? []);
  const [receiveRows, setReceiveRows] = useState<Transaction[]>(cached?.receiveRows ?? []);
  const [selfPayRows, setSelfPayRows] = useState<Transaction[]>(cached?.selfPayRows ?? []);
  const [userNames, setUserNames] = useState<Map<string, string>>(cached?.userNames ?? new Map());
  const [detail, setDetail] = useState<{ t: Transaction; stage: Stage } | null>(null);
  const [reason, setReason] = useState('');
  const [paying, setPaying] = useState<Transaction | null>(null);
  const [prepaying, setPrepaying] = useState<Transaction | null>(null);
  /** 精算する対象(1件でも複数でも同じ画面。2026-09-04 まとめて精算) */
  const [reimbursing, setReimbursing] = useState<{ items: Transaction[]; payeeId: string } | null>(null);
  /** まとめて精算モード(⑤の右上ボタン)。チェックで選び、立替者・月で絞り込める */
  const [batchMode, setBatchMode] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [fPayee, setFPayee] = useState('');
  const [fMonth, setFMonth] = useState('');
  const [fCo, setFCo] = useState('');
  const [attaching, setAttaching] = useState<{ t: Transaction; mode: 'receive' | 'settle' } | null>(null);
  /**
   * 支払い・仮払い・精算・受領の入力画面を、保存せずに「閉じる」で閉じたら元の詳細に戻す
   * (2026-10-14 指摘: 閉じると詳細まで消えて一覧に戻っていた)。
   * 保存できたとき(onDone)は申請が次の段階へ進むので、詳細には戻さない。
   * 成功時は onDone → onClose の順に呼ばれるため、state ではなく ref で持つ(同じ処理の中で最新値を読むため)
   */
  const returnTo = useRef<{ t: Transaction; stage: Stage } | null>(null);
  const openSub = (t: Transaction, stage: Stage, open: () => void) => {
    returnTo.current = { t, stage };
    open();
    setDetail(null);
  };
  const subDone = () => {
    returnTo.current = null;
  };
  const subClosed = () => {
    const back = returnTo.current;
    returnTo.current = null;
    if (back) setDetail(back);
  };
  /** 「読み込み中…」は初回(キャッシュなし)だけ。再読込は画面を保ったまま裏で行う */
  const [readyKey, setReadyKey] = useState(() => (flowCache.has(cacheKey) ? cacheKey : ''));
  const loading = readyKey !== cacheKey;
  const [busy, setBusy] = useState(false);
  const isMobile = useIsMobile();
  /**
   * PCの表は見出しで並べ替え・絞り込み(2026-10-14 発注者指示: スプレッドシートのように)。
   * 全セクション共通で効く。スマホはカード表示で見出しが無いため、従来の絞り込み欄を使う
   */
  const [sheetFilters, setSheetFilters] = useState<SheetFilters>({});
  const [sheetSort, setSheetSort] = useState<SheetSort>({ key: 'notified', dir: 'desc' });
  /** 繰り返し支払い(2026-09-07)の口座名を出すため。支払いページでのみ使う */
  const [cashAccounts, setCashAccounts] = useState<CashAccount[]>([]);
  useEffect(() => {
    if (page !== 'pay') return;
    fetchCashAccounts()
      .then(setCashAccounts)
      .catch(() => setCashAccounts([]));
  }, [page]);

  // 同じコンポーネントのままページ(承認⇔支払い等)が切り替わったときはキャッシュから即復元
  useEffect(() => {
    const c = flowCache.get(cacheKey);
    if (!c) return;
    setManagerRows(c.managerRows);
    setRecurringCheckRows(c.recurringCheckRows ?? []);
    setApprovalRows(c.approvalRows);
    setPaymentRows(c.paymentRows);
    setCompletionRows(c.completionRows);
    setReimbursementRows(c.reimbursementRows);
    setCashHandoverRows(c.cashHandoverRows);
    setReceiveRows(c.receiveRows);
    setSelfPayRows(c.selfPayRows);
    setUserNames(c.userNames);
    setReadyKey(cacheKey);
  }, [cacheKey]);

  // お知らせからの遷移(2026-08-27): state.openId の申請を、このページの該当セクションから探して
  // 詳細ポップアップ(操作ボタン付き)を自動で開く
  const location = useLocation();
  const openId = (location.state as { openId?: string } | null)?.openId ?? null;
  const [autoOpened, setAutoOpened] = useState(false);
  useEffect(() => {
    if (!openId || autoOpened) return;
    const candidates: Array<[Transaction[], Stage]> =
      page === 'approve'
        ? [
            [managerRows, 'manager'],
            [recurringCheckRows, 'recurring_check'],
            [approvalRows, 'approve'],
          ]
        : page === 'pay'
          ? [
              [selfPayRows, 'pay'],
              [paymentRows, 'pay'],
              [cashHandoverRows, 'cash_hand'],
              [reimbursementRows, 'reimburse'],
            ]
          : page === 'receive'
            ? [[receiveRows, 'receive']]
            : [[completionRows, 'complete']];
    for (const [rows, stage] of candidates) {
      const t = rows.find((r) => r.id === openId);
      if (t) {
        setDetail({ t, stage });
        setAutoOpened(true);
        return;
      }
    }
  }, [
    openId,
    autoOpened,
    page,
    managerRows,
    recurringCheckRows,
    approvalRows,
    paymentRows,
    selfPayRows,
    cashHandoverRows,
    reimbursementRows,
    receiveRows,
    completionRows,
  ]);
  // フィルター(複数選択。空=すべて)と並べ替え — 申請一覧と同じ仕様。全セクションに適用
  const [fCreator, setFCreator] = useState<string[]>([]);
  const [fCompany, setFCompany] = useState<string[]>([]);
  const [fDept, setFDept] = useState<string[]>([]);
  const [sortKey, setSortKey] = useState<SortKey>('notified');
  const [sortDesc, setSortDesc] = useState(true);
  /** 月でまとめる(支払い画面のみ・2026-09-25 経理要望) */
  const [groupMonth, setGroupMonth] = useState(() => groupMonthCache.get(cacheKey) ?? page === 'pay');
  /** 月ごとの開閉。「セクション:月」→ 開いているか(未設定はその月の既定に従う) */
  const [monthOpen, setMonthOpen] = useState<Map<string, boolean>>(
    () => new Map(monthOpenCache.get(cacheKey) ?? []),
  );
  useEffect(() => {
    setGroupMonth(groupMonthCache.get(cacheKey) ?? page === 'pay');
    setMonthOpen(new Map(monthOpenCache.get(cacheKey) ?? []));
  }, [cacheKey, page]);
  const changeGroupMonth = (on: boolean) => {
    setGroupMonth(on);
    groupMonthCache.set(cacheKey, on);
  };
  const changeMonthOpen = (next: Map<string, boolean>) => {
    setMonthOpen(next);
    monthOpenCache.set(cacheKey, [...next.entries()]);
  };
  /** 既定は今月まで開き、来月以降は閉じる(2026-09-25 指示。件数と合計は見出しに出るので隠れない) */
  const monthDefaultOpen = (m: string) => m === NO_MONTH || m <= todayStr().slice(0, 7);
  const isMonthOpen = (section: string, m: string) =>
    monthOpen.get(`${section}:${m}`) ?? monthDefaultOpen(m);
  const toggleMonth = (section: string, m: string) => {
    const next = new Map(monthOpen);
    next.set(`${section}:${m}`, !isMonthOpen(section, m));
    changeMonthOpen(next);
  };
  /** 支払い予定日の月ごとに分ける。月の並びは並べ替えの向き(新しい順/古い順)に合わせる */
  function monthGroups(rows: Transaction[]): { month: string; rows: Transaction[] }[] {
    const map = new Map<string, Transaction[]>();
    for (const t of rows) {
      const m = t.payment_date ? t.payment_date.slice(0, 7) : NO_MONTH;
      const list = map.get(m) ?? [];
      list.push(t);
      map.set(m, list);
    }
    const months = [...map.keys()].filter((m) => m !== NO_MONTH).sort();
    if (isMobile ? sortDesc : (sheetSort?.dir ?? 'desc') === 'desc') months.reverse();
    if (map.has(NO_MONTH)) months.push(NO_MONTH);
    return months.map((m) => ({ month: m, rows: map.get(m) as Transaction[] }));
  }

  const isBucho = flowRole === 'bucho';
  const isApprover = flowRole === 'approver';
  const isAccountingRole = flowRole === 'accounting';
  /** 部門長として動く(役割が部門長、または担当事業部を持つ承認者。2026-09-08 指示) */
  const managerDepts = user?.manager_department_ids ?? NO_DEPTS;
  const actsAsBucho = isBucho || (isApprover && managerDepts.length > 0);
  /** 繰り返し支払いを支払いページに出す日数(支払日の何日前から。既定7日・2026-09-08 指示) */
  const [rcDisplayDays, setRcDisplayDays] = useState(7);

  /** silent=true(自動の読み直し)のときは、失敗しても通知を出さない */
  const load = useCallback(async (opts?: { silent?: boolean }) => {
    if (!user) return;
    try {
      if (page === 'pay' && isAccountingRole) {
        fetchRecurringSettings()
          .then((s) => setRcDisplayDays(s.display_days_before))
          .catch(() => undefined);
      }
      const users = await fetchUsers();
      const names = new Map(users.map((u) => [u.id, u.display_name]));
      setUserNames(names);

      // ページごとに必要な一覧だけ取得する(2026-08-26 ページ分割)
      const [m, a, pRaw, c, r, cashRows, rec, rcc] = await Promise.all([
        page === 'approve' && actsAsBucho ? fetchForManagerCheck(managerDepts) : Promise.resolve([]),
        page === 'approve' && isApprover ? fetchForApproval() : Promise.resolve([]),
        page === 'pay'
          ? isAccountingRole
            ? fetchForPaymentByAccounting()
            : isBucho
              ? fetchForPaymentByBucho(user.manager_department_ids)
              : isApprover
                ? // 承認者: 部門長役の代行分 + 担当事業部の部門長分(2026-09-08)
                  Promise.all([
                    fetchForPaymentByApprover(user.id),
                    managerDepts.length > 0 ? fetchForPaymentByBucho(managerDepts) : Promise.resolve([] as Transaction[]),
                  ]).then(([a, b]) => {
                    const seen = new Set(a.map((t) => t.id));
                    return [...a, ...b.filter((t) => !seen.has(t.id))];
                  })
                : Promise.resolve([])
          : Promise.resolve([]),
        page === 'confirm' && isApprover ? fetchForCompletion() : Promise.resolve([]),
        page === 'pay' && isAccountingRole ? fetchForReimbursement() : Promise.resolve([]),
        page === 'pay' && isAccountingRole ? fetchForCashHandover() : Promise.resolve([]),
        page === 'receive'
          ? fetchForReceive(user.id, user.manager_department_ids, isApprover || isAccountingRole)
          : Promise.resolve([]),
        page === 'approve' && actsAsBucho ? fetchRecurringForManagerCheck(user.id, managerDepts) : Promise.resolve([]),
      ]);
      // 承認者の部門長役の支払い(p15)は、申請者が管理側(承認者・経理)の分だけ。
      // 一般メンバーの部門長立替は、その事業部の部門長が支払う
      const rolesById = new Map(users.map((u) => [u.id, roleOf(u) as string]));
      const p =
        page === 'pay' && isApprover
          ? pRaw.filter(
              (t) =>
                managerDepts.includes(t.department_id) ||
                ['approver', 'accounting'].includes(rolesById.get(t.created_by) ?? ''),
            )
          : pRaw;
      // 「自分の支払い」は支払いページの全員共通セクション(自分で支払う承認済み分)
      const selfPay = page === 'pay' ? await fetchForSelfPayment(user.id) : [];
      setManagerRows(m);
      setRecurringCheckRows(rcc);
      setApprovalRows(a);
      setPaymentRows(p);
      setCompletionRows(c);
      setReimbursementRows(r);
      setCashHandoverRows(cashRows);
      setReceiveRows(rec);
      setSelfPayRows(selfPay);
      flowCache.set(cacheKey, {
        managerRows: m,
        recurringCheckRows: rcc,
        approvalRows: a,
        paymentRows: p,
        completionRows: c,
        reimbursementRows: r,
        cashHandoverRows: cashRows,
        receiveRows: rec,
        selfPayRows: selfPay,
        userNames: names,
      });
      // 通知は「詳細・操作」を開いた時点で既読になる(2026-08-24/08-26)。
      // ここでは、このページ宛て(stage)の通知のうち、担当一覧に出てこない申請の分だけ
      // 自動既読にする(他の担当者が先に処理した等で、未読が永久に残るのを防ぐ)
      const visibleIds = new Set(
        [...m, ...rcc, ...a, ...p, ...c, ...r, ...cashRows, ...rec, ...selfPay].map((t) => t.id),
      );
      await markReadExceptStage(user.id, page, visibleIds);
    } catch (e) {
      if (opts?.silent) console.error('一覧の自動読み直しに失敗しました', e);
      else toast(e instanceof Error ? e.message : '一覧を取得できませんでした');
    } finally {
      setReadyKey(cacheKey);
    }
  }, [user, page, isBucho, isApprover, isAccountingRole, actsAsBucho, managerDepts, toast, cacheKey]);

  useEffect(() => {
    void load();
  }, [load]);

  // 通知が届いたら一覧も自動で最新化する(2026-08-26。ページの再読込は不要)。
  // タブに戻ってきたときも念のため読み直す。
  // 2026-09-17 改修依頼 B: 自分に通知が来ない変更(申請者の取消など)は開き直すまで出なかったため、
  // 別のウィンドウから戻ったときと、開いている間は1分ごとにも読み直す
  useEffect(() => {
    if (!user) return;
    let first = true;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let lastRefresh = Date.now();
    const refresh = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        lastRefresh = Date.now();
        void load({ silent: true });
      }, 800);
    };
    const unsubscribe = subscribeMyUnread(user.id, () => {
      if (first) {
        first = false;
        return;
      }
      refresh();
    });
    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    // ウィンドウの切り替えで何度も読まないよう、直前の読み直しから15秒はあける
    const onFocus = () => {
      if (Date.now() - lastRefresh > 15_000) refresh();
    };
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') refresh();
    }, 60_000);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);
    return () => {
      unsubscribe();
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
      clearInterval(interval);
      if (timer) clearTimeout(timer);
    };
  }, [user, load]);


  // 部門長立替への転換ボタン(①②)は廃止(2026-08-26 発注者指示)。
  // 部門長立替は申請時に「立替×部門長」を選んだ場合のみ
  async function run(tx: Transaction, action: 'check' | 'recurring_check' | 'approve' | 'complete') {
    if (!user) return;
    setBusy(true);
    try {
      if (action === 'check') {
        const result = await managerCheck({ transaction: tx, userId: user.id, advance: false });
        reportFlowResult(toast, `${tx.code} を確認しました。承認待ちに進みました`, result);
      } else if (action === 'recurring_check') {
        const result = await recurringManagerCheck({ transaction: tx, userId: user.id });
        reportFlowResult(toast, `${tx.code} を確認しました。承認者の完了確認に進みます`, result);
      } else if (action === 'approve') {
        const result = await approve({ transaction: tx, userId: user.id, advance: false });
        reportFlowResult(toast, `${tx.code} を承認しました。支払い者に通知します`, result);
      } else {
        const result = await complete({ transaction: tx, userId: user.id });
        // 完了後も承認者は修正できる(2026-09-17)。それ以外の人は編集できない
        reportFlowResult(toast, `${tx.code} を完了しました`, result);
      }
      setDetail(null);
      await load();
    } catch (e) {
      toast(e instanceof Error ? e.message : '操作に失敗しました');
      // ほかの人が先に処理していた場合は、古い内容のまま操作を続けないよう閉じて一覧を最新にする
      if (isFlowStaleError(e)) {
        setDetail(null);
        await load();
      }
    } finally {
      setBusy(false);
    }
  }

  async function doReject(tx: Transaction) {
    if (!user) return;
    setBusy(true);
    try {
      const result = await reject({ transaction: tx, userId: user.id, reason });
      reportFlowResult(toast, `${tx.code} を差戻しました。申請者に通知されます`, result);
      setDetail(null);
      setReason('');
      await load();
    } catch (e) {
      toast(e instanceof Error ? e.message : '差戻しに失敗しました');
      if (isFlowStaleError(e)) {
        setDetail(null);
        setReason('');
        await load();
      }
    } finally {
      setBusy(false);
    }
  }

  const name = (id: string | null | undefined) => (id ? (userNames.get(id) ?? '—') : '—');
  /** 繰り返し支払い(2026-09-07)は申請者がいないため、申請者欄に種別と内容を出す */
  const applicantCell = (t: Transaction) =>
    t.source === 'recurring' ? (
      <span title={t.description ?? ''} style={{ whiteSpace: 'nowrap' }}>
        繰り返し支払い: {t.description}
      </span>
    ) : (
      name(t.created_by)
    );
  const cashAccountName = (id: string | null | undefined) => {
    const a = id ? cashAccounts.find((x) => x.id === id) : undefined;
    return a ? formatCashAccountLabel(a, companyName(a.company_id)) : '—';
  };

  /** 繰り返し支払いの見送り(2026-09-07): この回だけ支払わない(取消として残す・CFの予定も消す) */
  async function skipOccurrence(t: Transaction) {
    if (!user) return;
    if (!window.confirm(`「${t.description}」(${toShortDate(t.payment_date)}・${formatYen(t.amount)}円)を今回は支払わないことにします。よろしいですか?`)) return;
    setBusy(true);
    try {
      await skipRecurringOccurrence({ transaction: t, userId: user.id });
      toast('この回を取り消しました(履歴には残ります)');
      await load();
    } catch (e) {
      toast(e instanceof Error ? e.message : '取消に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  /** 通知日 = その権限にステータスが回ってきた日(2-1) */
  function notifiedAt(t: Transaction, stage: Stage): unknown {
    switch (stage) {
      case 'manager':
        return t.applied_at;
      case 'recurring_check':
        return t.paid_at;
      case 'approve':
        return t.manager_checked_at ?? t.applied_at;
      case 'cash_hand':
        return t.approved_at;
      case 'pay':
        return t.status === 'received' ? t.received_at : t.approved_at;
      case 'reimburse':
        return t.paid_at;
      case 'receive':
        // 仮払い=仮払い日 / 立替=精算日 に受領依頼が届く
        return t.status === 'cash_ready' ? t.cash_handed_at : t.reimbursed_at;
      case 'complete':
        return t.reimbursed_at ?? t.paid_at;
    }
  }

  /** フィルター+並べ替えを各セクションの一覧に適用する */
  /** 表の列(見出しの並べ替え・絞り込みに使う)。通知日は段階によって意味が変わるため段階ごとに作る */
  function columnsFor(stage: Stage): SheetColumn<Transaction>[] {
    const cols: SheetColumn<Transaction>[] = [
      { key: 'applied', label: '申請日', text: (t) => tsToShortDate(t.applied_at), sortValue: (t) => tsToMillis(t.applied_at) },
      { key: 'notified', label: '通知日', text: (t) => tsToShortDate(notifiedAt(t, stage)), sortValue: (t) => tsToMillis(notifiedAt(t, stage)) },
      { key: 'creator', label: '申請者', text: (t) => (t.source === 'recurring' ? '繰り返し支払い' : name(t.created_by)) },
      { key: 'company', label: '会社', text: (t) => companyName(t.company_id ?? 'sum') },
      { key: 'dept', label: '事業部', text: (t) => departmentName(t.department_id) },
      { key: 'account', label: '科目①', text: (t) => (t.account_id ? accountName(t.account_id) : '(経理入力)') },
      { key: 'sub', label: '科目②', text: (t) => (t.sub_account_id ? subAccountName(t.sub_account_id) : '—') },
      { key: 'amount', label: '金額', text: (t) => formatYen(t.amount), sortValue: (t) => t.amount },
      { key: 'method', label: '支払い方法', text: (t) => (t.payment_method ? PAYMENT_METHOD_LABELS[t.payment_method] : '—') },
      { key: 'due', label: '支払い予定日', text: (t) => (t.paid_at ? '—' : toShortDate(t.payment_date)), sortValue: (t) => t.payment_date },
      { key: 'payer', label: '支払い者', text: (t) => (t.payer ? PAYER_LABELS[t.payer] : '—') },
    ];
    if (stage === 'complete') {
      cols.splice(10, 0, { key: 'paidDate', label: '支払日', text: (t) => (t.paid_at ? toShortDate(t.payment_date) : '—'), sortValue: (t) => t.payment_date });
    }
    return cols;
  }

  function applyView(rows: Transaction[], stage: Stage): Transaction[] {
    // PC: 見出しの絞り込み・並べ替え(2026-10-14)
    if (!isMobile) return applySheet(rows, columnsFor(stage), sheetFilters, sheetSort);
    const filtered = rows.filter(
      (t) =>
        (fCreator.length === 0 || fCreator.includes(t.created_by)) &&
        (fCompany.length === 0 || fCompany.includes(t.company_id ?? 'sum')) &&
        (fDept.length === 0 || fDept.includes(t.department_id)),
    );
    const dir = sortDesc ? -1 : 1;
    const value = (t: Transaction): string | number => {
      switch (sortKey) {
        case 'notified':
          return tsToMillis(notifiedAt(t, stage));
        case 'applied':
          return tsToMillis(t.applied_at);
        case 'payment':
          return t.payment_date;
        case 'amount':
          return t.amount;
      }
    };
    return [...filtered].sort((a, b) => {
      const va = value(a);
      const vb = value(b);
      return (va < vb ? -1 : va > vb ? 1 : 0) * dir;
    });
  }

  /** 申請者フィルターの候補(全セクションに登場する人。先頭に「自分」) */
  const allRows = [
    ...managerRows,
    ...approvalRows,
    ...paymentRows,
    ...completionRows,
    ...reimbursementRows,
    ...cashHandoverRows,
    ...receiveRows,
    ...selfPayRows,
  ];
  const creatorIds = [...new Set(allRows.map((t) => t.created_by))].filter((id) => id !== user?.id);
  const creatorOptions = [
    ...(user ? [{ value: user.id, label: '自分' }] : []),
    ...creatorIds
      .map((id) => ({ value: id, label: userNames.get(id) ?? id }))
      .sort((a, b) => a.label.localeCompare(b.label, 'ja')),
  ];
  const anyFilter = fCreator.length + fCompany.length + fDept.length > 0;

  /** 詳細ポップアップ内の操作(段階に応じて出し分け) */
  function detailActions(t: Transaction, stage: Stage) {
    // 支払い済み以降(完了確認)は差戻しできない: お金が動いているため、再申請の対象にならない。
    // 内容の修正は詳細の「修正(全項目)」から行う(2026-09-07。2026-09-17 からどの段階でも)
    // 差戻しは支払い前の段階だけ(2026-10-22 発注者決定: 支払い済みは不可・訂正は承認者の修正)。繰り返し支払いは対象外。
    // 差し戻せるのは承認者・経理・管理者と、その事業部の部門長(ルールと同じ条件)
    const canReject =
      isApprover ||
      isAccountingRole ||
      can('master_admin') ||
      (isBucho && (user?.manager_department_ids ?? []).includes(t.department_id));
    const rejectable =
      canReject && t.source !== 'recurring' && ['applied', 'manager_checked', 'approved', 'cash_ready', 'received'].includes(t.status);
    return (
      <>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {stage === 'recurring_check' && (
            <button className="btn-sm-ok" disabled={busy} onClick={() => void run(t, 'recurring_check')}>
              確認する(承認者の完了確認へ進める)
            </button>
          )}
          {stage === 'manager' && (
            <button className="btn-sm-ok" disabled={busy} onClick={() => void run(t, 'check')}>
              確認する(承認へ進める)
            </button>
          )}
          {stage === 'approve' && (
            <>
              <button
                className="btn-sm-ok"
                disabled={busy || t.created_by === user?.id}
                title={t.created_by === user?.id ? '自分の申請は自分で承認できません(もう一方の承認者が承認します)' : undefined}
                onClick={() => void run(t, 'approve')}
              >
                承認する
              </button>
              {t.created_by === user?.id && (
                <span className="hint">自分の申請のため、もう一方の承認者の承認待ちです。</span>
              )}
            </>
          )}
          {stage === 'cash_hand' && (
            <button
              className="btn-sm-ok"
              disabled={busy}
              onClick={() => openSub(t, stage, () => setPrepaying(t))}
            >
              仮払いする(口座を選ぶ)
            </button>
          )}
          {stage === 'pay' && t.status === 'cash_ready' && (
            <span className="hint">本人の受領待ちです(本人が申請一覧から受領書を添付して記録します)。</span>
          )}
          {stage === 'pay' && t.status !== 'cash_ready' && (
            <button
              className="btn-sm-ok"
              disabled={busy}
              onClick={() => openSub(t, stage, () => setPaying(t))}
            >
              {t.source === 'recurring'
                ? t.payment_method === 'direct_debit' || t.payment_method === 'card'
                  ? '引き落とし確認済みにする'
                  : '支払い詳細を入力する'
                : '支払い方法を入力する'}
            </button>
          )}
          {stage === 'receive' && t.status === 'cash_ready' && (
            <button
              className="btn-sm-ok"
              disabled={busy}
              onClick={() => openSub(t, stage, () => setAttaching({ t, mode: 'receive' }))}
            >
              受領を記録(記入済み受領書を添付)
            </button>
          )}
          {/* 立替の受領書は、経理がまとめて精算するときにその場でサイン/振込明細を記録する(2026-09-04)。
              本人が精算受領書を添付する経路は廃止 */}
          {stage === 'reimburse' && (
            <button
              className="btn-sm-ok"
              disabled={busy}
              onClick={() => openSub(t, stage, () => setReimbursing({ items: [t], payeeId: t.paid_by ?? t.created_by }))}
            >
              精算する
            </button>
          )}
          {/* 報告型(支払い済み申請)の完了は詳細ヘッダーの「承認者確認する(完了)」から(2026-08-27) */}
          {stage === 'complete' && t.source !== 'report' && (
            <button
              className="btn-sm-ok"
              disabled={busy || (t.advance === true && (!t.reimbursed_at || !t.settlement_received_at))}
              title={
                t.advance === true && (!t.reimbursed_at || !t.settlement_received_at)
                  ? '経理の精算(受領のサインまたは振込明細)が済むと完了確認できます'
                  : undefined
              }
              onClick={() => void run(t, 'complete')}
            >
              完了する(ロック)
            </button>
          )}
        </div>
        {stage === 'complete' && (
          <p className="hint" style={{ marginTop: 8 }}>
            金額・日付・科目などの修正は、上の「修正(全項目)」から行えます(完了後も修正できます)。支払い済みを差し戻すと、支払いの記録はそのまま残るので、資金繰り(CF)の調整が必要になることがあります。
          </p>
        )}
        {rejectable && (
          <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'center' }}>
            {/* フォントは小さめに固定(2026-08-28 指示: スマホでプレースホルダーが大きすぎた) */}
            <input
              style={{ flex: 1, padding: '8px 10px', border: '1px solid var(--line)', borderRadius: 6, fontFamily: 'inherit', fontSize: 13 }}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="差戻しの場合はコメント(理由)を入力 — 申請者に通知されます"
            />
            <button className="btn-sm-ng" disabled={busy || !reason.trim()} onClick={() => void doReject(t)}>
              差戻す
            </button>
          </div>
        )}
      </>
    );
  }

  /** ⑤ 立替精算の状態列(通常表示・まとめて精算モード共通) */
  const reimburseExtra = (t: Transaction) => (
    <span className="hint">支払日 {toShortDate(t.payment_date)} / {name(t.paid_by)}が立替</span>
  );

  /**
   * まとめて精算(2026-09-04 指示 → 2026-09-07 改修)。
   * 通常の表と同じ項目・同じ順番のまま、先頭にチェックボックスだけを足す。
   * 立替者・月(立替払いをした日の月)で絞り込み、チェックした分をまとめて精算する。
   * 一度に精算できるのは同じ立替者の分だけ(受領のサインが1人分のため)
   */
  function batchSettleList(rows: Transaction[]) {
    const payeeOf = (t: Transaction) => t.paid_by ?? t.created_by;
    const monthOf = (t: Transaction) => t.payment_date.slice(0, 7);
    const payees = [...new Set(rows.map(payeeOf))].sort((a, b) => name(a).localeCompare(name(b), 'ja'));
    const months = [...new Set(rows.map(monthOf))].sort().reverse();
    // 会社ごとに精算するため、会社でも絞り込める(2026-09-07 指示)
    const companyOf = (t: Transaction) => t.company_id ?? 'sum';
    const cos = [...new Set(rows.map(companyOf))];
    const shown = rows.filter(
      (t) => (!fPayee || payeeOf(t) === fPayee) && (!fMonth || monthOf(t) === fMonth) && (!fCo || companyOf(t) === fCo),
    );
    const selectedRows = rows.filter((t) => selected.has(t.id));
    const selectedPayee = selectedRows[0] ? payeeOf(selectedRows[0]) : null;
    const total = selectedRows.reduce((s, t) => s + t.amount, 0);
    const canPick = (t: Transaction) => selectedPayee == null || payeeOf(t) === selectedPayee;
    const toggle = (t: Transaction) => {
      setSelected((prev) => {
        const next = new Set(prev);
        if (next.has(t.id)) next.delete(t.id);
        else next.add(t.id);
        return next;
      });
    };
    const selectAllShown = () => {
      const payee = selectedPayee ?? (shown[0] ? payeeOf(shown[0]) : null);
      setSelected(new Set(shown.filter((t) => payeeOf(t) === payee).map((t) => t.id)));
    };
    const allShownSelected = shown.length > 0 && shown.every((t) => selected.has(t.id));
    const monthLabel = (m: string) => `${m.slice(0, 4)}年${Number(m.slice(5, 7))}月`;
    return (
      <div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
          <select value={fPayee} onChange={(e) => setFPayee(e.target.value)} style={{ minWidth: 150 }}>
            <option value="">立替者: すべて</option>
            {payees.map((id) => (
              <option key={id} value={id}>
                {name(id)}
              </option>
            ))}
          </select>
          <select value={fMonth} onChange={(e) => setFMonth(e.target.value)} style={{ minWidth: 140 }}>
            <option value="">月: すべて</option>
            {months.map((m) => (
              <option key={m} value={m}>
                {monthLabel(m)}
              </option>
            ))}
          </select>
          <select value={fCo} onChange={(e) => setFCo(e.target.value)} style={{ minWidth: 120 }}>
            <option value="">会社: すべて</option>
            {cos.map((c) => (
              <option key={c} value={c}>
                {companyName(c)}
              </option>
            ))}
          </select>
          <button className="txn-link" onClick={selectAllShown}>
            表示分をすべて選ぶ
          </button>
          {selected.size > 0 && (
            <button className="txn-link" onClick={() => setSelected(new Set())}>
              選択を解除
            </button>
          )}
        </div>
        {shown.length === 0 ? (
          <p className="empty">条件に合う立替はありません。</p>
        ) : (
          sectionTable(shown, 'reimburse', reimburseExtra, {
            head: (
              <input
                type="checkbox"
                style={{ width: 'auto' }}
                aria-label="表示分をすべて選ぶ"
                checked={allShownSelected}
                onChange={() => (allShownSelected ? setSelected(new Set()) : selectAllShown())}
              />
            ),
            cell: (t) => (
              <input
                type="checkbox"
                style={{ width: 'auto' }}
                checked={selected.has(t.id)}
                disabled={!canPick(t)}
                title={!canPick(t) ? '一度に精算できるのは同じ立替者の分だけです' : undefined}
                onChange={() => toggle(t)}
              />
            ),
          })
        )}
        <div className="modal-actions" style={{ marginTop: 12 }}>
          <button
            className="btn-green"
            // 文言が長いので折り返しを許す(スマホで枠からはみ出さないように。2026-09-06)
            style={{ whiteSpace: 'normal', lineHeight: 1.4 }}
            disabled={selectedRows.length === 0 || !selectedPayee}
            onClick={() => selectedPayee && setReimbursing({ items: selectedRows, payeeId: selectedPayee })}
          >
            {selectedRows.length > 0
              ? isMobile
                ? `${selectedRows.length}件(${formatYen(total)}円)をまとめて精算する`
                : `${name(selectedPayee)} の ${selectedRows.length}件(${formatYen(total)}円)をまとめて精算する`
              : '精算する分にチェックを入れてください'}
          </button>
        </div>
      </div>
    );
  }

  /** 見出しの絞り込みの選択肢を作る元(段階ごとの全件)。ほかの列の絞り込みは SheetTh 側で反映する */
  function rawRowsFor(stage: Stage): Transaction[] {
    switch (stage) {
      case 'manager':
        return managerRows;
      case 'recurring_check':
        return recurringCheckRows;
      case 'approve':
        return approvalRows;
      case 'cash_hand':
        return cashHandoverRows;
      case 'pay':
        return [...selfPayRows, ...paymentRows.filter((t) => t.source !== 'recurring')];
      case 'reimburse':
        return reimbursementRows;
      case 'receive':
        return receiveRows;
      case 'complete':
        return completionRows;
    }
  }
  function sheetTh(stage: Stage, key: string, style?: React.CSSProperties) {
    const cols = columnsFor(stage);
    const column = cols.find((c) => c.key === key);
    if (!column) return null;
    return (
      <SheetTh
        key={key}
        column={column}
        columns={cols}
        rows={rawRowsFor(stage)}
        filters={sheetFilters}
        onFilters={setSheetFilters}
        sort={sheetSort}
        onSort={setSheetSort}
        style={style}
      />
    );
  }

  /** 先頭に足す列(まとめて精算のチェックボックス。2026-09-07 指示: 通常の表と同じ項目・順番のまま選べるように) */
  type LeadCol = { head: React.ReactNode; cell: (t: Transaction) => React.ReactNode };
  /**
   * groupSection を渡した一覧は「月でまとめる」の対象になる(支払い画面のセクションのみ)。
   * 渡さないページ(承認・受領・完了確認)は今までどおり1つの表で出す
   */
  function sectionTable(
    rows: Transaction[],
    stage: Stage,
    extraCol?: (t: Transaction) => React.ReactNode,
    leadCol?: LeadCol,
    groupSection?: string,
  ) {
    const grouped = !!groupSection && groupMonth;
    const groups = grouped ? monthGroups(rows) : [{ month: '', rows }];
    const opened = (m: string) => !grouped || isMonthOpen(groupSection as string, m);
    const groupSum = (list: Transaction[]) => list.reduce((s, t) => s + t.amount, 0);
    // スマホはA案カード表示(2026-08-28 発注者選定): 1申請=1カード・横スクロールなし
    if (isMobile) {
      const card = (t: Transaction) => (
            <div
              key={t.id}
              onClick={() => {
                setReason('');
                setDetail({ t, stage });
                if (user) void markReadForTransaction(user.id, t.id);
              }}
              style={{
                background: '#fff',
                border: '1px solid var(--line)',
                borderRadius: 10,
                padding: '9px 12px',
                marginBottom: 8,
                cursor: 'pointer',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                {leadCol && (
                  <span onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex' }}>
                    {leadCol.cell(t)}
                  </span>
                )}
                <b style={{ fontSize: 13 }}>{applicantCell(t)}</b>
                {extraCol ? extraCol(t) : <StatusBadge t={t} />}
                {t.source === 'report' && <span className="hint" style={{ fontSize: 9.5 }}>支払い済み申請</span>}
                <span style={{ marginLeft: 'auto', fontSize: 15, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                  {formatYen(t.amount)}円 <span style={{ color: 'var(--muted)', fontWeight: 400 }}>›</span>
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 5, fontSize: 11, color: 'var(--muted)', flexWrap: 'wrap' }}>
                <span>{t.account_id ? accountName(t.account_id) : '(経理入力)'}</span>
                <span>{t.payment_method ? PAYMENT_METHOD_LABELS[t.payment_method] : '—'}</span>
                <span>申請 {tsToShortDate(t.applied_at)}</span>
              </div>
            </div>
      );
      return (
        <div>
          {/* カード全体をタップで詳細を開く(2026-08-28 指示。「詳細・操作」リンクは廃止) */}
          {groups.map((g) => (
            <Fragment key={g.month}>
              {grouped && (
                <button
                  className="month-sep-sp"
                  aria-expanded={opened(g.month)}
                  onClick={() => toggleMonth(groupSection as string, g.month)}
                >
                  <span>
                    {opened(g.month) ? '▾' : '▸'} {monthTitle(g.month)}
                  </span>
                  <span className="hint">
                    {g.rows.length}件 / {formatYen(groupSum(g.rows))}円
                  </span>
                </button>
              )}
              {opened(g.month) && g.rows.map(card)}
            </Fragment>
          ))}
        </div>
      );
    }
    // PC: 列構成(2-1指定): 申請日/通知日/申請者/会社/事業部/科目①/科目②/金額/支払い方法/支払い予定日/支払い者
    return (
      <table className="tx-table">
        <thead>
          <tr>
            {/* 曜日併記(■13)で日付が広がったため列幅を明示(■15 見切れ対策・2026-08-28) */}
            {leadCol && <th style={{ width: 30 }}>{leadCol.head}</th>}
            {sheetTh(stage, 'applied', { width: 76 })}
            {sheetTh(stage, 'notified', { width: 76 })}
            {sheetTh(stage, 'creator')}
            {sheetTh(stage, 'company', { width: 52 })}
            {sheetTh(stage, 'dept')}
            {sheetTh(stage, 'account')}
            {sheetTh(stage, 'sub', { width: 56 })}
            {sheetTh(stage, 'amount', { width: 74 })}
            {sheetTh(stage, 'method', { width: 76 })}
            {sheetTh(stage, 'due', { width: 92 })}
            {stage === 'complete' && sheetTh(stage, 'paidDate', { width: 76 })}
            {sheetTh(stage, 'payer', { width: 70 })}
            {extraCol && <th>状態</th>}
            <th style={{ width: 86 }} />
          </tr>
        </thead>
        <tbody>
          {groups.map((g) => (
            <Fragment key={g.month}>
              {grouped && (
                <tr className="month-sep">
                  {/* 列は表の全幅に広げる(月の区切り行。列幅が月をまたいでもズレないよう表の中に入れる) */}
                  <td colSpan={12 + (leadCol ? 1 : 0) + (stage === 'complete' ? 1 : 0) + (extraCol ? 1 : 0)}>
                    <button
                      className="txn-link"
                      aria-expanded={opened(g.month)}
                      onClick={() => toggleMonth(groupSection as string, g.month)}
                    >
                      {opened(g.month) ? '▾' : '▸'} {monthTitle(g.month)}
                    </button>
                    <span className="hint" style={{ marginLeft: 8 }}>
                      {g.rows.length}件 / 合計 {formatYen(groupSum(g.rows))}円
                    </span>
                  </td>
                </tr>
              )}
              {opened(g.month) &&
                g.rows.map((t) => (
            <tr key={t.id}>
              {leadCol && <td>{leadCol.cell(t)}</td>}
              <td>{tsToShortDate(t.applied_at)}</td>
              <td>{tsToShortDate(notifiedAt(t, stage))}</td>
              <td title={name(t.created_by)}>{applicantCell(t)}</td>
              <td className="hint">{companyName(t.company_id ?? 'sum')}</td>
              <td>{departmentName(t.department_id)}</td>
              <td className="hint">{t.account_id ? accountName(t.account_id) : '(経理入力)'}</td>
              <td className="hint">{t.sub_account_id ? subAccountName(t.sub_account_id) : '—'}</td>
              <td className="num">{formatYen(t.amount)}</td>
              <td>{t.payment_method ? PAYMENT_METHOD_LABELS[t.payment_method] : '—'}</td>
              <td>{t.paid_at ? '—' : toShortDate(t.payment_date)}</td>
              {stage === 'complete' && <td>{t.paid_at ? toShortDate(t.payment_date) : '—'}</td>}
              <td>
                {t.payer ? PAYER_LABELS[t.payer] : '—'}
                {t.advance === true && t.payer === 'bucho' && <span className="hint">(立替)</span>}
                {/* 報告型は同じ一覧に出るため種別を明示(2026-08-27) */}
                {t.source === 'report' && (
                  <span className="hint" style={{ display: 'block', fontSize: 9.5 }}>支払い済み申請</span>
                )}
              </td>
              {extraCol && <td>{extraCol(t)}</td>}
              <td style={{ whiteSpace: 'nowrap' }}>
                <button
                  className="btn-line"
                  style={{ padding: '4px 8px', fontSize: 11.5 }}
                  onClick={() => {
                    setReason('');
                    setDetail({ t, stage });
                    // お知らせ(info)のみ既読化。操作依頼はアクション完了まで残す(2026-08-27)
                    if (user) void markReadForTransaction(user.id, t.id);
                  }}
                >
                  詳細・操作
                </button>
              </td>
            </tr>
                ))}
            </Fragment>
          ))}
        </tbody>
      </table>
    );
  }


  if (loading) {
    return (
      <section className="screen">
        <div className="card">
          <p className="empty">読み込み中…</p>
        </div>
      </section>
    );
  }

  const mRows = applyView(managerRows, 'manager');
  const rcCheckRows = applyView(recurringCheckRows, 'recurring_check');
  const aRows = applyView(approvalRows, 'approve');
  // 繰り返し支払い(2026-09-07)は経理の支払い一覧から分けて、支払日順の専用枠に出す。
  // 支払日の前日(設定の日数前)になった分だけ出し、お知らせと同じタイミングにする(2026-09-08 指示)
  const pRows = applyView(paymentRows.filter((t) => t.source !== 'recurring'), 'pay');
  const rcLimitDate = addDays(todayStr(), Math.max(0, rcDisplayDays));
  const rcRows = [
    ...applyView(
      paymentRows.filter((t) => t.source === 'recurring' && t.payment_date <= rcLimitDate),
      'pay',
    ),
  ].sort((a, b) => (a.payment_date < b.payment_date ? -1 : 1));
  const cRows = applyView(completionRows, 'complete');
  const rRows = applyView(reimbursementRows, 'reimburse');
  const chRows = applyView(cashHandoverRows, 'cash_hand');
  const recRows = applyView(receiveRows, 'receive');
  const spRows = applyView(selfPayRows, 'pay');

  /** 「すべて開く/すべて閉じる」の対象(支払い画面に出ている月のまとまり) */
  const payMonthKeys =
    page === 'pay'
      ? [
          ...monthGroups(spRows).map((g) => `self:${g.month}`),
          ...monthGroups(pRows).map((g) => `pay:${g.month}`),
          ...monthGroups(chRows).map((g) => `cash_hand:${g.month}`),
          ...monthGroups(rRows).map((g) => `reimburse:${g.month}`),
        ]
      : [];
  const setAllMonths = (openAll: boolean) =>
    changeMonthOpen(new Map(payMonthKeys.map((k) => [k, openAll])));

  return (
    <section className="screen" style={{ maxWidth: 1200 }}>
      {/* フィルターと並べ替え(全セクションに適用。申請一覧と同じ仕様) */}
      <div className="card" style={{ padding: '12px 22px' }}>
        <div className="toolbar">
          {/* 絞り込み行・並べ替え行に分け、それぞれ左詰め(2026-08-28 指示) */}
          {/* PCは表の見出しで絞り込み・並べ替え(2026-10-14)。上の欄はスマホ用 */}
          {!isMobile && (
            <span className="tool-filter">
              <span className="hint">表の見出し(項目名)を押すと、並べ替えと絞り込みができます</span>
              {countSheetFilters(sheetFilters) > 0 && (
                <button className="txn-link" onClick={() => setSheetFilters({})}>
                  絞り込みをすべて解除({countSheetFilters(sheetFilters)}列)
                </button>
              )}
            </span>
          )}
          {isMobile && (
          <span className="tool-filter">
            <span className="hint">絞り込み</span>
            <MultiSelectFilter label="申請者" options={creatorOptions} selected={fCreator} onChange={setFCreator} />
            <MultiSelectFilter
              label="会社"
              options={companies.map((c) => ({ value: c.id, label: c.name }))}
              selected={fCompany}
              onChange={setFCompany}
            />
            <MultiSelectFilter
              label="事業部"
              options={departments.map((d) => ({ value: d.id, label: d.name }))}
              selected={fDept}
              onChange={setFDept}
            />
            {anyFilter && (
              <button
                className="txn-link"
                onClick={() => {
                  setFCreator([]);
                  setFCompany([]);
                  setFDept([]);
                }}
              >
                すべて解除
              </button>
            )}
          </span>
          )}
          {isMobile && (
          <span className="tool-sort">
            <span className="hint">並べ替え</span>
            <select value={sortKey} onChange={(e) => setSortKey(e.target.value as SortKey)}>
              {SORT_OPTIONS.map((o) => (
                <option key={o.key} value={o.key}>
                  {o.label}
                </option>
              ))}
            </select>
            <select value={sortDesc ? 'desc' : 'asc'} onChange={(e) => setSortDesc(e.target.value === 'desc')}>
              <option value="desc">新しい順 / 大きい順</option>
              <option value="asc">古い順 / 小さい順</option>
            </select>
          </span>
          )}
          {/* 月でまとめる(2026-09-25 経理要望)。支払い画面のみ */}
          {page === 'pay' && (
            <span className="tool-sort">
              <span className="hint">まとめ</span>
              <label
                className="month-group-toggle"
                title="支払い予定日(立替精算は立替払い日)の月ごとにまとめ、月単位で開閉できます"
              >
                <input
                  type="checkbox"
                  style={{ width: 'auto' }}
                  checked={groupMonth}
                  onChange={(e) => changeGroupMonth(e.target.checked)}
                />
                月でまとめる
              </label>
              {groupMonth && payMonthKeys.length > 0 && (
                <>
                  <button className="txn-link" onClick={() => setAllMonths(true)}>
                    すべて開く
                  </button>
                  <button className="txn-link" onClick={() => setAllMonths(false)}>
                    すべて閉じる
                  </button>
                </>
              )}
            </span>
          )}
        </div>
      </div>

      {page === 'approve' && actsAsBucho && (
        <div className="card">
          <h3>① 部門長確認({isBucho ? '自分の事業部のみ' : '担当する事業部'})</h3>
          {mRows.length === 0 ? (
            <p className="empty">{managerRows.length === 0 ? '確認待ちの申請はありません。' : '条件に合う申請がありません。'}</p>
          ) : (
            sectionTable(mRows, 'manager')
          )}
        </div>
      )}

      {/* 繰り返し支払いの部門長確認(2026-10-15 発注者指示)。経理が支払い済みの分を、担当事業部の部門長が確認する。
          確認すると承認者の完了確認へ進む。対象があるときだけ枠を出す */}
      {page === 'approve' && actsAsBucho && recurringCheckRows.length > 0 && (
        <div className="card">
          <h3>①-2 部門長確認(繰り返し支払い — 経理が支払い済み)</h3>
          {rcCheckRows.length === 0 ? (
            <p className="empty">条件に合う支払いがありません。</p>
          ) : (
            sectionTable(rcCheckRows, 'recurring_check', (t) => <span className="hint">支払日 {toShortDate(t.payment_date)}</span>)
          )}
        </div>
      )}

      {page === 'approve' && isApprover && (
        <div className="card">
          <h3>② 承認(承認者)</h3>
          {aRows.length === 0 ? (
            <p className="empty">{approvalRows.length === 0 ? '承認待ちの申請はありません。' : '条件に合う申請がありません。'}</p>
          ) : (
            sectionTable(aRows, 'approve', (t) => (
              <span className="badge b-ok">済({name(t.manager_checked_by)})</span>
            ))
          )}
        </div>
      )}

      {page === 'pay' && (
        <div className="card">
          <h3>自分の支払い(自分で支払う承認済みの申請)</h3>
          {spRows.length === 0 ? (
            <p className="empty">{selfPayRows.length === 0 ? '自分が支払う申請はありません。' : '条件に合う申請がありません。'}</p>
          ) : (
            sectionTable(
              spRows,
              'pay',
              (t) =>
                t.status === 'received' ? (
                  <span className="badge b-pend">仮払い金で支払い</span>
                ) : (
                  <span className="hint">期日 {toShortDate(t.payment_date)}</span>
                ),
              undefined,
              'self',
            )
          )}
        </div>
      )}

      {page === 'pay' && (isAccountingRole || isBucho || isApprover) && (
        <div className="card">
          <h3>
            {isAccountingRole
              ? '③ 支払い(経理 — 担当分のみ)'
              : isBucho
                ? '③ 支払い(部門長 — 依頼・部門長立替分)'
                : managerDepts.length > 0
                  ? '③ 支払い(承認者 — 担当事業部の部門長分・部門長役の立替分)'
                  : '③ 支払い(承認者 — 部門長役の立替分)'}
          </h3>
          {pRows.length === 0 ? (
            <p className="empty">{paymentRows.length === 0 ? '支払い待ちの申請はありません。' : '条件に合う申請がありません。'}</p>
          ) : (
            sectionTable(
              pRows,
              'pay',
              (t) => <span className="hint">期日 {toShortDate(t.payment_date)}</span>,
              undefined,
              'pay',
            )
          )}
        </div>
      )}

      {page === 'pay' && isAccountingRole && (
        <div className="card">
          <h3>③-2 仮払い(経理 — 仮払いの申請)</h3>
          {chRows.length === 0 ? (
            <p className="empty">{cashHandoverRows.length === 0 ? '仮払い待ちの申請はありません。' : '条件に合う申請がありません。'}</p>
          ) : (
            sectionTable(chRows, 'cash_hand', undefined, undefined, 'cash_hand')
          )}
        </div>
      )}

      {/* 繰り返し支払い(2026-09-08 本番反映)。支払い待ちがあるときだけ枠を出す */}
      {page === 'pay' && isAccountingRole && rcRows.length > 0 && (
        <div className="card">
          <h3>③-3 繰り返し支払い(経理 — 家賃・リースなどの定期支払い)</h3>
          {rcRows.length === 0 ? (
            <p className="empty">支払い待ちの繰り返し支払いはありません。</p>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>支払日</th>
                  <th>内容</th>
                  <th>会社・事業部</th>
                  <th>科目</th>
                  <th className="num">金額</th>
                  <th>支払方法</th>
                  <th>口座</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rcRows.map((t) => (
                  <tr key={t.id}>
                    <td>
                      {toShortDate(t.payment_date)}
                      {t.payment_date <= todayStr() && (
                        <span className="badge b-pend" style={{ marginLeft: 6 }}>
                          期日
                        </span>
                      )}
                    </td>
                    <td>{t.description}</td>
                    <td className="hint">
                      {companyName(t.company_id ?? 'sum')} / {departmentName(t.department_id)}
                    </td>
                    <td className="hint">{t.account_id ? accountName(t.account_id) : '—'}</td>
                    <td className="num">
                      {formatYen(t.amount)}
                    </td>
                    <td>{t.payment_method ? PAYMENT_METHOD_LABELS[t.payment_method] : '—'}</td>
                    <td className="hint">{cashAccountName(t.payment_account_id)}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <button
                        className="btn-line"
                        style={{ padding: '4px 8px', fontSize: 11.5 }}
                        onClick={() => {
                          setReason('');
                          setDetail({ t, stage: 'pay' });
                          if (user) void markReadForTransaction(user.id, t.id);
                        }}
                      >
                        詳細・操作
                      </button>
                      <button className="txn-link" onClick={() => void skipOccurrence(t)} disabled={busy}>
                        今回は支払わない
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <p className="hint" style={{ marginTop: 10 }}>
            支払日の{rcDisplayDays}日前になった分をここに出します。経理へのお知らせは支払日の前日に届きます(どちらの日数もマスタ管理→設定で変えられます)。
            それより先の分は「繰り返し支払い」画面と資金繰り(CF)で確認できます。
            振込は振り込んだら「詳細・操作」→「支払い詳細を入力する」、口座引落は引き落としを確認したら「詳細・操作」→「引き落とし確認済みにする」で確定してください。
            その後、事業部の部門長の確認、承認者の完了確認を経てロックされます。
          </p>
        </div>
      )}

      {page === 'pay' && isAccountingRole && (
        <div className="card">
          {/* まとめて精算(2026-09-04 指示): 枠の右上のボタンでチェックが出現し、人・月で絞って選べる */}
          <h3 style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            ⑤ 立替精算(経理 — 立替者への精算)
            {reimbursementRows.length > 0 && (
              <button
                className="btn-line"
                style={{ marginLeft: 'auto' }}
                onClick={() => {
                  setBatchMode((v) => !v);
                  setSelected(new Set());
                }}
              >
                {batchMode ? 'まとめて精算をやめる' : 'まとめて精算'}
              </button>
            )}
          </h3>
          {rRows.length === 0 ? (
            <p className="empty">{reimbursementRows.length === 0 ? '精算待ちの立替はありません。' : '条件に合う申請がありません。'}</p>
          ) : batchMode ? (
            batchSettleList(rRows)
          ) : (
            sectionTable(rRows, 'reimburse', reimburseExtra, undefined, 'reimburse')
          )}
        </div>
      )}

      {page === 'confirm' && isApprover && (
        <div className="card">
          <h3>④ 完了確認(承認者)</h3>
          {cRows.length === 0 ? (
            <p className="empty">{completionRows.length === 0 ? '完了確認待ちの申請はありません。' : '条件に合う申請がありません。'}</p>
          ) : (
            sectionTable(cRows, 'complete', (t) =>
              t.advance === true && !t.reimbursed_at ? (
                <span className="badge b-pend">精算待ち</span>
              ) : t.advance === true && !t.settlement_received_at ? (
                <span className="badge b-pend">受領書待ち</span>
              ) : (
                <span className="badge b-ok">支払 {toShortDate(t.payment_date)}</span>
              ),
            )
          )}
        </div>
      )}

      {page === 'receive' && (
        <div className="card">
          <h3>受領(自分が受領を記録するもの)</h3>
          {recRows.length === 0 ? (
            <p className="empty">{receiveRows.length === 0 ? '受領を記録する申請はありません。' : '条件に合う申請がありません。'}</p>
          ) : (
            sectionTable(recRows, 'receive', (t) =>
              t.status === 'cash_ready' ? (
                <span className="badge b-pend">仮払い金の受領待ち</span>
              ) : (
                <span className="badge b-pend">経理の精算待ち</span>
              ),
            )
          )}
        </div>
      )}

      {detail && (
        <ApplicationDetailModal
          transaction={detail.t}
          onClose={() => setDetail(null)}
          actions={detailActions(detail.t, detail.stage)}
          // 経理修正・完了前修正・報告の確認のあと一覧を読み直す(2026-09-16: 修正しても一覧が古いままだった)
          onChanged={() => void load()}
        />
      )}

      {paying && (
        <PaymentModal
          transaction={paying}
          onClose={() => {
            setPaying(null);
            subClosed();
          }}
          onDone={() => {
            subDone();
            void load();
          }}
        />
      )}
      {prepaying && (
        <PrepayModal
          transaction={prepaying}
          onClose={() => {
            setPrepaying(null);
            subClosed();
          }}
          onDone={() => {
            subDone();
            void load();
          }}
        />
      )}
      {attaching && (
        <ReceiptAttachModal
          transaction={attaching.t}
          mode={attaching.mode}
          onClose={() => {
            setAttaching(null);
            subClosed();
          }}
          onDone={() => {
            subDone();
            void load();
          }}
        />
      )}
      {reimbursing && (
        <BatchSettleModal
          transactions={reimbursing.items}
          payeeId={reimbursing.payeeId}
          payeeName={name(reimbursing.payeeId)}
          onClose={() => {
            setReimbursing(null);
            subClosed();
          }}
          onDone={() => {
            subDone();
            setSelected(new Set());
            void load();
          }}
        />
      )}
    </section>
  );
}
